import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ErrorModal from "../modals/ErrorModal";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";

const OtpForm = ({
  mobilePhone,
  idCard,
  consentStatus,
  otpRef,
  onSubmit,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const { authToken, userId } = useLiffProfile();
  const [error, setError] = useState(null);
  const [consentOtpRef, setConsentOtpRef] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const navigate = useNavigate();

  // เพิ่ม ref สำหรับ guard ไม่ให้ call ซ้ำ
  const hasRequestedOtp = useRef(false);

  useEffect(() => {
    hasRequestedOtp.current = false; // reset guard เมื่อ otpRef เปลี่ยน (เช่นกดขอรหัสใหม่)
  }, [otpRef, mobilePhone, idCard, consentStatus]);

  useEffect(() => {
    // console.log("📲 mobilePhone:", mobilePhone, consentStatus);
    // console.log("🆔 idCard:", idCard);
    if (otpRef) {
      setConsentOtpRef(otpRef);
    }
  }, []);

  const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
  const apiUrlVerify =
    process.env.REACT_APP_API_URL + "/api/users/verify?replace=true";
  const apiUrlConfirm = process.env.REACT_APP_API_URL + "/api/users/confirm";

  useEffect(() => {
    const handleConsentAccept = async () => {
      const postData = { consentStatus, idCard, mobilePhone };
      try {
        const response = await axios.put(apiUrlVerify, postData, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-Client-Auth": clientAuth,
            "x-user-id": userId,
            "Accept-Language": i18n.language.toUpperCase(),
          },
        });
        if (response.data.statusCode === 200 && response.data.data) {
          const { otpRef } = response.data.data;
          if (!otpRef) {
            setError("ไม่สามารถรับ OTP ได้ กรุณาลองใหม่");
            return;
          }
          setConsentOtpRef(otpRef);
        } else {
          setError(response.data.message || "ไม่สามารถยืนยันได้ กรุณาลองใหม่");
        }
      } catch (err) {
        setError("เกิดข้อผิดพลาด กรุณาลองใหม่");
      }
    };

    // ต้องมีค่า consentStatus/phone/idCard และยังไม่ได้ยิง
    if (mobilePhone && idCard && consentStatus && !hasRequestedOtp.current) {
      hasRequestedOtp.current = true; // กั้น call ซ้ำ
      handleConsentAccept();
      setCountdown(300);
    }
  }, [consentStatus, mobilePhone, idCard]);

  const fetchOtp = async () => {
    setIsLoading(true);
    const putData = {
      consentStatus,
      idCard,
      mobilePhone,
    };

    try {
      const response = await axios.put(apiUrlConfirm, putData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": clientAuth,
          "Content-Type": "application/json",
          "x-user-id": userId,
          "Accept-Language": i18n.language.toUpperCase(),
        },
      });

      if (response.data.statusCode === 200 && response.data.data) {
        const { otpRef } = response.data.data;
        if (!otpRef) {
          setError("ไม่สามารถรับ OTP ได้ กรุณาลองใหม่");
          return;
        }
        setConsentOtpRef(otpRef);
        navigate("/successBindingForm");
      } else {
        setError(response.data.message || "ไม่สามารถขอ OTP ได้ กรุณาลองใหม่");
      }
    } catch (err) {
      setError(t("otp.request_error") || "ไม่สามารถขอ OTP ได้ กรุณาลองใหม่");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (/[^0-9]/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    const joinedOtp = newOtp.join("");
    if (joinedOtp.length === 6 && newOtp.every((d) => d !== "")) {
      handleVerify(newOtp.join(""));
    }
  };

  const handleVerify = async (otpValueParam) => {
    const otpValue = otpValueParam || otp.join("");
    if (otpValue.length === 6) {
      setIsLoading(true);
      const finalPayload = {
        consentOtpRef,
        consentOtp: otpValue,
        consentStatus,
        idCard,
        mobilePhone,
      };
      try {
        const response = await axios.put(apiUrlConfirm, finalPayload, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-Client-Auth": clientAuth,
            "Content-Type": "application/json",
            "x-user-id": userId,
            "Accept-Language": i18n.language.toUpperCase(),
          },
        });

        const { statusCode, message, data } = response.data;
        switch (statusCode) {
          case 200:
            // refresh user
            onSubmit(finalPayload);
            setIsLoading(true);
            navigate("/SuccessBindingForm", { state: finalPayload });
            break;
          case 400:
            switch (message) {
              case "INCORRECT_OTP":
                setError(t("incorrect_otp"));
                setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
                break;
              case "EXPIRED_OTP":
                setError(t("expired_otp"));
                setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
                break;
              default:
                setError(message);
                setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
            }
            break;
          case 404:
            setError(message);
            setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
            break;
          case 406:
            setError(t("register_error_code_406"));
            setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
            break;
          case 409:
            setError(t("register_error_code_409"));
            setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
            break;
          default:
            setError(message);
            setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
        }
      } catch (err) {
        setError("เกิดข้อผิดพลาดขณะยืนยัน OTP กรุณาลองใหม่");
      } finally {
        setIsLoading(false);
      }
    } else {
      setError("กรุณากรอก OTP ให้ถูกต้อง");
    }
  };

  const handleRequestNewOtp = async () => {
    setOtp(["", "", "", "", "", ""]);
    setError(null);
    setCountdown(300); // รีเซ็ตเวลา
    setIsLoading(true);

    const postData = {
      consentStatus,
      idCard: idCard?.trim(),
      mobilePhone: mobilePhone?.trim(),
    };

    // ตรวจสอบค่าก่อนยิง API
    if (!postData.idCard || !postData.mobilePhone) {
      setError("ไม่สามารถขอรหัส OTP ได้ เพราะข้อมูลไม่ครบ");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.put(apiUrlVerify, postData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
          "Accept-Language": i18n.language.toUpperCase(),
        },
      });

      if (response.data.statusCode === 200 && response.data.data) {
        const { otpRef } = response.data.data;
        if (!otpRef) {
          setError("ไม่สามารถรับ OTP ได้ กรุณาลองใหม่");
          return;
        }
        setConsentOtpRef(otpRef);
      } else {
        setError(response.data.message || "ไม่สามารถขอ OTP ได้ กรุณาลองใหม่");
      }
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการขอ OTP กรุณาลองใหม่");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(null); // ใช้กับ ErrorModal
  };

  const handleCloseOtp = () => {
    setError(null); // รีเซ็ต error ด้วย
    onClose(); // ปิด Popup OTP
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-lg relative">
        <button
          className="absolute top-2 right-3 text-gray-500 hover:text-gray-700 text-xl"
          onClick={handleCloseOtp}
        >
          &times;
        </button>
        <div className="w-full text-3xl font-semibold">
          {t("validate_otp_header")}
        </div>
        <h2 className="w-full font-normal text-lg">
          {t("validate_otp_description", {
            phone: `${mobilePhone?.slice(0, 3)}-xxx-${mobilePhone?.slice(6)}`,
          })}
        </h2>

        {consentOtpRef && (
          <div className="w-full flex flex-col items-center gap-2 mb-2 mt-4">
            <div className="h-6 text-primary-text text-base font-medium">
              {t("otp_reference_code")} : {consentOtpRef}
            </div>
          </div>
        )}

        <div className="flex justify-center mb-4 mt-2">
          {otp.map((digit, i) => (
            <input
              key={i}
              id={`otp-input-${i}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={digit}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                if (val.length > 1) return; // กันกรณีพิมพ์รวดเดียว/แปะหลายตัว
                handleOtpChange({ target: { value: val } }, i);
              }}
              onKeyDown={(e) => {
                // กันไม่ให้กดตัวอักษร
                if (
                  e.key.length === 1 && // เฉพาะ key ตัวอักษร/ตัวเลข
                  !/[0-9]/.test(e.key) && // ถ้าไม่ใช่เลข 0-9
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData
                  .getData("text")
                  .replace(/[^0-9]/g, "");
                if (pasted.length === otp.length) {
                  // วางทีเดียว 6 หลัก
                  setOtp(pasted.split(""));
                  // focus ออก
                  document
                    .getElementById(`otp-input-${otp.length - 1}`)
                    ?.blur();
                  e.preventDefault();
                }
              }}
              maxLength={1}
              className="w-12 h-16 text-center border border-gray-300 mx-1 text-2xl rounded-lg"
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          className="w-full mt-4 px-6 py-3 bg-primary text-white rounded-full hover:bg-green-600"
        >
          {isLoading ? t("loading") : t("confirm")}
        </button>
        {countdown > 0 && (
          <div className="text-sm text-gray-500 text-center mt-1">
            รหัสจะหมดอายุใน {Math.floor(countdown / 60)}:
            {String(countdown % 60).padStart(2, "0")} นาที
          </div>
        )}
        {countdown === 0 && (
          <div className="flex justify-center items-center mt-2">
            <button
              className="underline text-base font-medium text-primary"
              onClick={handleRequestNewOtp}
              type="button"
            >
              {t("resend_otp")}
            </button>
          </div>
        )}
        {isLoading && <LoadingBar />}
        {error && <ErrorModal error={error} onClose={handleCloseError} />}
      </div>
    </div>
  );
};

export default OtpForm;
